import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Tab, Tabs } from '@mui/material';

import { VMenu } from '../../components/shared';
import { ToolsDamInformation, CharacteristicsProject, FilesTable } from '../../components/custom';
import { LayoutBase } from '../../layouts';
import { ConstructiveProjectServices, IConstructiveProject } from '../../services/api/constructive-project/ConstructiveProjectServices';
import { ConstructiveStepsServices, IConstructiveSteps } from '../../services/api/constructive-project/ConstructiveStepsServices';
import { CustomAlert } from '../../components/shared/VAlert';
import { IFileServices, FileServicesBack } from '../../services/api/files/FileServicesBack';
import { useAuthContext } from '../../context';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function tabProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export const TechnicalCharacteristicsProject = () => {

  const url = useParams();
  const navigate = useNavigate();
  const [value, setValue] = React.useState(0);
  const [dataProject, setDataProject] = useState<IConstructiveProject>();
  const [dataSteps, setDataSteps] = useState<IConstructiveSteps[]>([]);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const { userData } = useAuthContext();
  const [alertInfo, setAlertInfo] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const characteristicsPermissionsString = useMemo(() => {
    return userData?.nivel_acesso?.documentacao_tecnica?.caracteristicas;
  }, [userData]);

  const hasCharacteristicsPermission = useCallback((action: 'add' | 'edit' | 'remove' | 'view'): boolean => {
    if (typeof characteristicsPermissionsString === 'string' && characteristicsPermissionsString !== '') {
      return characteristicsPermissionsString.includes(action);
    }
    return false;
  }, [characteristicsPermissionsString]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  function handleEdit() {
    if (dataProject?.uuid) {
      const id = dataProject.uuid;
      navigate(`./${id}`);
    }
  }
  function handleNew() {
    navigate('./new');
  }

  useEffect(() => {
    fetchDataProject();
  }, []);

  const toggleDialog = () => {
    setDialogOpen(!isDialogOpen);
  };

  const fetchDataProject = () => {
    if (url.dam) {
      ConstructiveProjectServices.get(url.dam, false)
        .then((result) => {
          if (result instanceof Error) {
            return result.message;
          } else {
            setDataProject(result);
            if (result.uuid) {
              fetchDataSteps(result.uuid);
            }
          }
        });
    }
  };

  const fetchDataSteps = (uuid: string) => {
    if (uuid) {
      ConstructiveStepsServices.get(uuid)
        .then((result) => {
          if (result instanceof Error) {
            return result.message;
          } else {
            setDataSteps(result);
          }
        });
    }
  };

  const [historico, setHistorico] = useState<boolean>(false);
  const [dataFilter, setDataFilter] = useState<IFileServices[]>();
  const [data, setData] = useState<IFileServices[]>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    fetchData();
  }, [url, historico]);

  async function sortedData() {
    if (dataFilter) {
      const sorted = dataFilter.slice().sort((a, b) => {
        if (a.data_emissao && b.data_emissao) {
          const dateA = new Date(a.data_emissao).getTime();
          const dateB = new Date(b.data_emissao).getTime();
          if (sortDirection === 'asc') {
            setSortDirection('desc');
            return dateA - dateB;
          } else {
            setSortDirection('asc');
            return dateB - dateA;
          }
        }
        return 0;
      });
      setDataFilter(sorted);
    }
  }

  const fetchData = () => {
    if (url.dam) {
      if (!historico) {
        FileServicesBack.get(url.dam)
          .then((result) => {
            if (result instanceof Error) {
              setAlertInfo({ open: true, message: 'Não foi possível obter os dados dos documentos! ' + result.message, severity: 'error' });
            } else {
              const value = result.filter(index => index.status === true);
              setData(value);
              setDataFilter(value);
            }
          });
      } else {
        FileServicesBack.get(url.dam)
          .then((result) => {
            if (result instanceof Error) {
              setAlertInfo({ open: true, message: 'Não foi possível obter os dados dos documentos! ' + result.message, severity: 'error' });
            } else {
              setData(result);
              setDataFilter(result);
            }
          });
      }
    }
  };


  const showAlert = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setAlertInfo({ open: false, message: '', severity: 'info' });
    setTimeout(() => {
      setAlertInfo({ open: true, message, severity });
    }, 100);
  };

  async function handleDelete(valueId: string | undefined) {
    if (valueId !== undefined) {
      FileServicesBack.deleteById(valueId)
        .then((result) => {
          if (result instanceof Error) {
            showAlert('Não foi possível excluir o documento! ' + result.message, 'error');
          } else {
            showAlert('Documento excluído!', 'success');
            fetchData();
            setDialogOpen(false);
          }
        });
    }
  }

  function handleFileType(newValue: string[] | null) {
    if (newValue !== null) {
      if (newValue.length > 0) {
        if (dataFilter) {
          const filteredData = dataFilter.filter(index => {
            const tipo = index.tipo?.tipo || '';
            const normalizedTipo = tipo.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
            return newValue.some(value => {
              const normalizedValue = value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
              return normalizedTipo.includes(normalizedValue);
            });
          });
          setDataFilter(filteredData);
        }
      } else {
        setDataFilter(data);
      }
    } else {
      setDataFilter(data);
    }
  }

  function handleHistorico() {
    setHistorico(!historico);
  }

  function handleEditFile(key: string | undefined) {
    if (key !== undefined) {
      navigate(`/information_dam/${url.dam}/files/file/${key}`);
    }
  }

  return (
    <LayoutBase title='Informações'
      tollBar={
        <ToolsDamInformation />
      }
    >
      <Box width={'100%'}>
        <VMenu id={url.dam} />
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }} justifyContent={'space-around'} display={'flex'}>
          <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons allowScrollButtonsMobile>
            <Tab label="CARACTERÍSTICAS TÉCNICAS DO PROJETO E DA CONSTRUÇÃO" {...tabProps(0)} />
            {/* <Tab label="ARQUIVOS" {...tabProps(1)} /> */}
          </Tabs>
        </Box>
        <CustomTabPanel value={value} index={0}>
          <CharacteristicsProject
            dataProject={dataProject}
            dataSteps={dataSteps}
            handleEdit={handleEdit}
            handleNew={handleNew}
            hasAddPermission={hasCharacteristicsPermission('add')}
            hasEditPermission={hasCharacteristicsPermission('edit')}
          />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <Box width={'100%'}>
            <FilesTable
              dataFilter={dataFilter}
              handleDelete={handleDelete}
              handleFileType={handleFileType}
              handleHistorico={handleHistorico}
              sortedData={sortedData}
              url={url}
              pagePermissions={characteristicsPermissionsString}
              title='Arquivos Projeto e Construção'
              isDialogOpen={isDialogOpen}
              toggleDialog={toggleDialog}
              handleEditFile={handleEditFile}
            />
          </Box>
        </CustomTabPanel>
      </Box>
      {alertInfo.open &&
        <CustomAlert
          message={alertInfo.message}
          severity={alertInfo.severity}
        />
      }
    </LayoutBase>
  );
};